import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useNavigationGuard } from './StepValidationContext'
import type { LibraTicket } from './ticket/libraTicketsApi'
import { useInactiveStepIds } from './ticket/useInactiveStepIds'
import type { LibraWorkflowStep } from './workflow/workflowStepsApi'
import { useWorkflow } from './WorkflowContext'

interface StepNavigationContextType {
  activeStepIndex: number
  setActiveStepIndex: (index: number) => void
  activeStep: LibraWorkflowStep | undefined
  maxNavigableIndex: number
  /**
   * Inactive steps are not navigable unless `allowInactive` is set, which a
   * deep link uses to open one read-only.
   */
  navigateToStep: (
    stepId: string,
    options?: { allowInactive?: boolean }
  ) => void
}

const StepNavigationContext = createContext<
  StepNavigationContextType | undefined
>(undefined)

export const StepNavigationProvider: FC<{
  ticket: LibraTicket
  children: ReactNode
}> = ({ ticket, children }) => {
  const workflow = useWorkflow()
  const { guardNavigation } = useNavigationGuard()

  const maxNavigableIndex = useMemo(
    () => workflow.stepIndices.get(ticket.stepId) ?? 0,
    [ticket.stepId, workflow.stepIndices]
  )

  const [activeStepIndex, setActiveStepIndexRaw] =
    useState<number>(maxNavigableIndex)

  useEffect(() => {
    setActiveStepIndexRaw(maxNavigableIndex)
  }, [maxNavigableIndex])

  const activeStep = workflow.steps[activeStepIndex]

  const inactiveStepIds = useInactiveStepIds()
  const inactiveStepIdsRef = useRef(inactiveStepIds)
  inactiveStepIdsRef.current = inactiveStepIds

  // Re-checks inactivity when the guarded continuation runs, which can be
  // after a save that changed the field values.
  const moveToIndex = useCallback(
    (index: number, allowInactive: boolean) => {
      guardNavigation(() => {
        const stepId = workflow.steps[index]?.id
        if (
          !allowInactive &&
          stepId !== undefined &&
          inactiveStepIdsRef.current.has(stepId)
        ) {
          return
        }
        setActiveStepIndexRaw(index)
      })
    },
    [guardNavigation, workflow.steps]
  )

  const setActiveStepIndex = useCallback(
    (index: number) => moveToIndex(index, false),
    [moveToIndex]
  )

  const navigateToStep = useCallback(
    (stepId: string, options?: { allowInactive?: boolean }) => {
      const index = workflow.stepIndices.get(stepId)
      if (index !== undefined) {
        moveToIndex(index, options?.allowInactive ?? false)
      }
    },
    [workflow.stepIndices, moveToIndex]
  )

  const value = useMemo<StepNavigationContextType>(
    () => ({
      activeStepIndex,
      setActiveStepIndex,
      activeStep,
      maxNavigableIndex,
      navigateToStep,
    }),
    [
      activeStepIndex,
      setActiveStepIndex,
      activeStep,
      maxNavigableIndex,
      navigateToStep,
    ]
  )

  return (
    <StepNavigationContext.Provider value={value}>
      {children}
    </StepNavigationContext.Provider>
  )
}

export function useStepNavigation(): StepNavigationContextType {
  const ctx = useContext(StepNavigationContext)
  if (!ctx) {
    throw new Error(
      'useStepNavigation must be used within a StepNavigationProvider'
    )
  }
  return ctx
}
